import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import FloorMap from './FloorMap';
import TodayScreen from './TodayScreen';
import OrderScreen from './OrderScreen';
import { ORDER_CHANGED, ORDER_PROGRESS_UPDATED, notifyOrderChanged } from './useOrderWorkflow';
import ListScreen from './ListScreen';
import RackSheet from './RackSheet';
import SettingsDialog from './SettingsDialog';
import GalleryDialog from './GalleryDialog';
import { useLayout } from './useLayout';
import {
  QUERY_KEYS, TODAY_KIND, buildMoves, countedMoves, monthDay, movesOfFixture, newReference,
  readNokeError, skuImageIndex, walkFixtureIds, weekdayLabel,
} from './format';
import {
  RestockV2Addable, addableOrderVariant, orderCopyText, koreaOrderLines, koreaOrderedLines, koreaTotalKept,
} from './orderPlan';
import {
  EMPTY_VETO, VetoControls, VetoState, applyVeto, dropDecidedRows, excludedByRack, excludedStyles,
  keptByRack, moveSkipKey, pinnedVariantIds, vetoIsEmpty,
} from './exclusions';
import {
  EMPTY_LIST_PATCH, ListPatch, applyListPatch, approvedRows, counterList, keptVariants, listPatchIsEmpty,
  mainsReady, pendingDecisions, variantsReady,
} from './counterList';
import {
  EMPTY_ORDER_EDIT, OrderEdit, addVariant, applyOrderEdit, notOrdered, removeAddedVariant,
  omitAllLines, resetLineQty, restoreAllLines, setVariantQty, toggleOrderLine as toggleOrderLineEdit,
} from './orderEdit';
import { useVeto } from './useVeto';
import { useCounterList } from './useCounterList';
import { useCounterPool } from './useCounterPool';
import { useSourcing } from './SinsangLinks';
import { C, MONO } from './tokens';
import {
  RestockV2CounterOrder, RestockV2DoneRecord, RestockV2Excluded, RestockV2Move, RestockV2OrderLine,
  RestockV2OrderVariant, RestockV2Params, RestockV2ParamKey, RestockV2Plan, RestockV2ThumbTarget,
  RestockV2Why,
} from './types';

// 選品 is appended rather than inserted: the three screens the part-timers use keep the positions
// they have learned, and the owner-only curation screen sits at the end.
type ScreenKey = 'map' | 'today' | 'order' | 'list';
const SCREEN_KEYS: ScreenKey[] = ['map', 'today', 'order', 'list'];
const SCREEN_LABEL: Record<ScreenKey, string> = {
  map: '衣架地圖', today: '今天', order: '訂貨', list: '選品',
};
const SCREEN_STORAGE = 'noke-restock-v2-screen';
const ORDER_STORAGE = 'noke-counter-order-pending';
type PendingOrder = { client_ref: string; lines: ReturnType<typeof koreaOrderedLines>; text: string };
const readPendingOrder = (): PendingOrder | null => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(ORDER_STORAGE) || 'null');
    return saved && typeof saved.client_ref === 'string' && Array.isArray(saved.lines) ? saved : null;
  } catch { return null; }
};

// How long after the LAST decision the engine is asked to recompute. Long enough that a run of
// yes/no taps is one recompute, short enough that he is not looking at 數字待重算 while thinking.
export const REFRESH_DEBOUNCE_MS = 1500;
// A POST is still open when the debounce fires: check again this often rather than fetching a plan
// that is already behind.
const WRITE_SETTLE_MS = 250;

// Every line the 訂貨 screen lets the owner take out: the Korea groups plus the Taiwan-warehouse
// rows. The 沒有登記進貨店家 rows are deliberately NOT here — nobody can order them anyway.
const orderableLines = (plan: RestockV2Plan | null): RestockV2OrderLine[] => [
  ...koreaOrderLines(plan?.order), ...(plan?.order?.warehouse || []),
];

const readScreen = (): ScreenKey => {
  try {
    const saved = sessionStorage.getItem(SCREEN_STORAGE) as ScreenKey | null;
    return saved && SCREEN_KEYS.indexOf(saved) >= 0 ? saved : 'map';
  } catch { return 'map'; }
};

const RestockV2Tab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { desktop, wide } = useLayout();
  // `served` is what the endpoint said; `plan` is that plus whatever veto is still in flight.
  const [served, setServed] = useState<RestockV2Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [screen, setScreen] = useState<ScreenKey>(readScreen);
  const [params, setParams] = useState<Partial<RestockV2Params>>({});
  const [copyText, setCopyText] = useState<string | null>(null);
  const [doneRows, setDoneRows] = useState<Record<string, boolean>>({});
  const [pendingRows, setPendingRows] = useState<Record<string, boolean>>({});
  const [whyRows, setWhyRows] = useState<Record<string, boolean>>({});
  const [openZones, setOpenZones] = useState<Record<string, boolean>>({});
  // Two different things: the phone opens a rack as a full-screen sheet, the desktop selects one
  // into the map's right-hand column.
  const [rackId, setRackId] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paramError, setParamError] = useState('');
  const [counterOrder, setCounterOrder] = useState<RestockV2CounterOrder | null>(null);
  const pendingOrder = useRef<PendingOrder | null>(readPendingOrder());
  const [createdOrderText, setCreatedOrderText] = useState<string | null>(null);
  const submittingOrder = useRef(false);
  const [orderUncertain, setOrderUncertain] = useState(!!pendingOrder.current);
  const [busy, setBusy] = useState(false);
  // Magnus 2026-09-17: 「if i remove one, don't make it refresh everything」. /restock-v2 is ~6.3s on
  // prod, so three states instead of one:
  //   loading    the FIRST load only — the one time there is nothing on screen to keep.
  //   refreshing a recompute running UNDER the rendered plan. Never blanks anything.
  //   stale      he has answered something the engine has not recomputed. The aggregates it feeds
  //              (件數, 秋冬 %, 帶回 ranking, the Korea order) are labelled 數字待重算 rather than
  //              redrawn from a guess, and 重新計算 forces it now.
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors whether any write is still in flight, so a debounce that fires mid-POST waits instead of
  // fetching a plan that is about to be out of date again.
  const writesBusy = useRef(false);
  // 復原 has to reverse a real stock write, so the card remembers the client_ref /done was written
  // with and the 12-hour window it came back with (POST /restock-v2/undo).
  const [doneRefs, setDoneRefs] = useState<Record<string, RestockV2DoneRecord>>({});
  const [why, setWhy] = useState<RestockV2Why | null>(null);
  const [whyLoading, setWhyLoading] = useState(false);
  const [whyError, setWhyError] = useState('');
  // One gallery for all three screens; a row's photo is the only thing that opens it.
  const [galleryTarget, setGalleryTarget] = useState<RestockV2ThumbTarget | null>(null);
  // What the buyer changed about THIS order: which lines he is not sending, and any quantity he set
  // himself. Tab state on purpose — see orderEdit.ts for the storage decision. `fetchPlan` never
  // touches it, so a recompute landing mid-order (and one lands 1.5s after any write) cannot take
  // his edits away; only leaving the page does, which is what 「這次」 means.
  const [orderEdit, setOrderEdit] = useState<OrderEdit>(EMPTY_ORDER_EDIT);
  const requestId = useRef(0);
  // `fetchPlan` has to close the two optimistic windows, but both hooks are constructed below it.
  const vetoRef = useRef<(landed: VetoState) => void>(() => {});
  const listRef = useRef<(landed: ListPatch) => void>(() => {});
  // What a recompute is ENTITLED to retire is fixed at the moment it is requested, so the optimistic
  // state has to be readable from inside `fetchPlan` (which is declared above both hooks).
  const vetoStateRef = useRef<VetoState>(EMPTY_VETO);
  const listPatchRef = useRef<ListPatch>(EMPTY_LIST_PATCH);
  // Monotonic count of writes STARTED. A landing plan may only clear 數字待重算 if this has not moved
  // since it was asked for; otherwise it is answering a question that is already out of date.
  const writeSeq = useRef(0);
  // Mirrors doneRows so the "this rack is finished" nudge can read it after an await without
  // doing side effects inside a state updater.
  const doneRef = useRef<Record<string, boolean>>({});
  useEffect(() => { doneRef.current = doneRows; }, [doneRows]);

  useEffect(() => {
    try { sessionStorage.setItem(SCREEN_STORAGE, screen); } catch { /* the picked screen stays in memory */ }
  }, [screen]);

  const query = useMemo(() => {
    const out: Record<string, number> = {};
    QUERY_KEYS.forEach((key: RestockV2ParamKey) => {
      const value = params[key];
      if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    });
    return out;
  }, [params]);

  const fetchPlan = useCallback(async (quiet = false) => {
    const id = ++requestId.current;
    // Snapshot taken HERE, not when the response lands: this is exactly the set of answers the
    // engine could have been computing from. Everything decided after this line is newer than the
    // plan that comes back, and survives it.
    const vetoAtRequest = vetoStateRef.current;
    const listAtRequest = listPatchRef.current;
    const doneAtRequest = doneRef.current;
    const seqAtRequest = writeSeq.current;
    // The spinner replaces the screen ONLY when there is no screen yet.
    if (quiet) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const response = await api.get<RestockV2Plan>('/api/noke/restock-v2', { params: query });
      if (requestId.current !== id) return;
      // The 排除 decisions ride IN this response (plan.excluded + meta.excluded_counts), so a
      // refetch can never silently re-add a style the owner took out — the server holds them.
      setServed(response.data);
      // This plan knows about everything answered before it was ASKED FOR — and nothing else. It
      // retires exactly that snapshot; a decision taken during the ~6.3s it was in flight is newer
      // than the plan and stays on screen (Magnus 2026-09-17: 「why it reverted back」).
      setStale(writeSeq.current !== seqAtRequest);
      vetoRef.current(vetoAtRequest);
      listRef.current(listAtRequest);
      // 做好了 ticks are write-backed too, and the same rule applies with a sharper edge: clearing a
      // tick this plan predates puts 做好了 back on a card that is already done, which invites a
      // SECOND stock write.
      setDoneRows((old) => dropDecidedRows(old, doneAtRequest));
      setDoneRefs((old) => dropDecidedRows(old, doneAtRequest));
      setPendingRows({});
      setWhyRows({});
      setOpenZones({});
      // `counterOrder` is deliberately NOT cleared here. It names a row that exists on the server,
      // and a recompute lands 1.5s after the create — clearing it would take 復原 off the screen in
      // the second he is most likely to want it. It clears when he undoes it, or when he leaves.
    } catch (err) {
      if (requestId.current === id) {
        const message = readNokeError(err, '無法取得補貨計算，請重試。');
        const status = (err as { response?: { status?: number } })?.response?.status;
        // A 400 on a run with overrides is a rejected ⚙ value: keep the last good plan on screen and
        // hand the server's own zh-TW sentence back to the dialog.
        if (status === 400 && Object.keys(query).length) { setParamError(message); setSettingsOpen(true); }
        else { setServed(null); setError(message); }
      }
    } finally {
      if (requestId.current === id) { setLoading(false); setRefreshing(false); }
    }
  }, [query]);

  /**
   * What every write calls instead of awaiting a recompute. A burst of yes/no/移除 costs ONE
   * /restock-v2, not N: each answer restarts the timer, so the engine runs once, REFRESH_DEBOUNCE_MS
   * after the last one. `requestId` still supersedes, so a slow debounced fetch that lands after a
   * newer one cannot overwrite it.
   */
  const scheduleRefresh = useCallback((immediate = false) => {
    // Counted BEFORE the timer: a recompute already in flight must not come back and announce that
    // the numbers are current, because this write is not in it.
    writeSeq.current += 1;
    setStale(true);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const tick = () => {
      // A POST started after the last schedule may still be open; fetching now would hand back a
      // plan that is already behind. Wait for it rather than flickering.
      if (writesBusy.current) { refreshTimer.current = setTimeout(tick, WRITE_SETTLE_MS); return; }
      refreshTimer.current = null;
      fetchPlan(true);
    };
    refreshTimer.current = setTimeout(tick, immediate ? 0 : REFRESH_DEBOUNCE_MS);
  }, [fetchPlan]);

  /** 重新計算 — he does not want to wait for the debounce. Same quiet fetch, no blanking. */
  const refreshNow = useCallback(() => {
    if (refreshTimer.current) { clearTimeout(refreshTimer.current); refreshTimer.current = null; }
    fetchPlan(true);
  }, [fetchPlan]);

  useEffect(() => {
    const refresh = () => { void fetchPlan(true); };
    window.addEventListener(ORDER_CHANGED, refresh);
    window.addEventListener(ORDER_PROGRESS_UPDATED, refresh);
    return () => { window.removeEventListener(ORDER_CHANGED, refresh); window.removeEventListener(ORDER_PROGRESS_UPDATED, refresh); };
  }, [fetchPlan]);
  useEffect(() => { fetchPlan(); return () => { requestId.current += 1; }; }, [fetchPlan]);
  useEffect(() => () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); }, []);

  // 不要這款 / 今天不做 / 加回 / 復原. Server state, shared between Magnus and Adam; the only thing
  // held locally is the optimistic window between the tap and the refetch.
  const veto = useVeto(scheduleRefresh);
  // 選品's six writes (加入清單／不要這款／換下／留著／移除／加回 + the 全部照建議 batch + the picker's
  // search). Same contract as the veto: server state, refetch decides, nothing kept in the browser.
  const listWrites = useCounterList(scheduleRefresh);
  // 看更多 — the ranked pool below the engine's bar (Magnus 2026-09-17: 「建議加入 only have 2
  // items … i need more」). It writes through the SAME /list/decide as a proposal card, so an
  // answer given there is stored identically — but it holds its OWN decided set and its OWN paging,
  // because `fetchPlan` retires the shared patch and never refetches this endpoint: deriving the
  // pool's hidden rows from that patch would hand him back a style he just decided.
  const pool = useCounterPool(listWrites.decide);
  // fetchPlan is defined above the hooks, so the two `retire`s reach it through refs rather than by
  // re-declaring the fetch below them — as do the states it snapshots at request time.
  vetoRef.current = veto.retire;
  listRef.current = listWrites.retire;
  vetoStateRef.current = veto.state;
  listPatchRef.current = listWrites.patch;
  writesBusy.current = Object.keys(veto.pending).length > 0
    || Object.keys(listWrites.pending).length > 0;
  // Two optimistic layers, two chokepoints: the veto verbs (rack tiles, 訂貨, the 選品 chips) and
  // the 選品 list verbs. Neither touches a derived aggregate — that is what 數字待重算 is for.
  const plan = useMemo(
    () => applyOrderEdit(
      applyListPatch(applyVeto(served, veto.state), listWrites.patch), orderEdit,
    ),
    [served, veto.state, listWrites.patch, orderEdit],
  );
  // What 訂貨 dims right now: only the styles whose veto has not come back yet. A persisted one is
  // already out of order.groups and lives in the 已排除 fold instead.
  // What leaves this order: the lines he unticked 這次不訂, plus any 不要這款 still in flight. ONE
  // set, because the copy text, the totals and the /ordered body all take exactly one — so a piece
  // can never be in the text and out of the total.
  const excluded = useMemo(
    () => notOrdered(orderEdit, veto.state.plan) as RestockV2Excluded,
    [orderEdit, veto.state.plan],
  );
  const excludedByFixture = useMemo(() => excludedByRack(excludedStyles(plan)), [plan]);
  // 留著 rows with no tile on their rail any more, bucketed the same way 已排除 styles are. A pin
  // he can no longer see is a pin he cannot take off, so these are merged into the rack's 留著 fold
  // rather than left to the 選品 screen alone.
  const keptByFixture = useMemo(() => keptByRack(keptVariants(counterList(plan))), [plan]);
  // The numbers are out of date the MOMENT a row moves optimistically, not when the server agrees —
  // so the label is driven by the optimistic windows themselves, plus `stale` for the gap between a
  // confirmed write and the debounced recompute.
  const numbersPending = stale || !vetoIsEmpty(veto.state) || !listPatchIsEmpty(listWrites.patch);
  // One bundle for the two 不要 verbs, for the RACK surfaces only (the desktop map column and the
  // phone sheet). 今天 deliberately gets none of it: staff execute the list, they do not decide it.
  // Every rack surface then shares one pair of verbs and one busy map. `variantsReady` false (table not migrated) collapses the panel back to the
  // original single-verb dialog rather than offering a button that can only answer 503.
  const vetoControls: VetoControls = useMemo(() => ({
    onStyle: veto.excludeStyle,
    onVariant: veto.excludeVariant,
    onSkipBringBack: veto.skipBringBack,
    onUnskipBringBack: veto.unskipBringBack,
    onKeep: veto.keepVariant,
    onUnkeep: veto.unkeepVariant,
    onMain: veto.setMainVariant,
    onUnmain: veto.clearMainVariant,
    mainPinned: (sku, served) => pinnedVariantIds(sku, served, veto.state),
    mainsReady: mainsReady(counterList(plan)),
    variantsReady: variantsReady(counterList(plan)),
    pending: veto.pending,
  }), [
    veto.excludeStyle, veto.excludeVariant, veto.skipBringBack, veto.unskipBringBack,
    veto.keepVariant, veto.unkeepVariant, veto.setMainVariant, veto.clearMainVariant,
    veto.state, veto.pending, plan,
  ]);
  // 取消留著 from a fold row: the row carries the pair, the write only needs the pair.
  const unkeepRow = useCallback(
    (row: { sku: string; variant_id: number }) => { veto.unkeepVariant(row.sku, row.variant_id); },
    [veto],
  );
  const skipBusy = useCallback((move: RestockV2Move) => !!veto.pending[moveSkipKey(move)], [veto.pending]);

  const moves = useMemo(() => buildMoves(plan), [plan]);
  const walk = useMemo(() => walkFixtureIds(plan), [plan]);
  const thumbIndex = useMemo(() => skuImageIndex(plan), [plan]);
  const todo = useMemo(() => countedMoves(moves), [moves]);
  // The walk starts at the first rack that actually has work; the rest stay folded on a phone.
  const firstZone = todo.length ? todo[0].fixtureId : null;

  const copy = useCallback(async (text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar('已複製清單', { variant: 'success' });
    } catch {
      setCopyText(text);
    }
  }, [enqueueSnackbar]);

  const markDone = useCallback(async (move: RestockV2Move) => {
    const line = move.line;
    if (line.variant_id === null || line.variant_id === undefined) {
      enqueueSnackbar('這筆沒有色碼編號，請到「庫存」頁處理。', { variant: 'warning' });
      return;
    }
    setPendingRows((old) => ({ ...old, [move.id]: true }));
    setDoneRows((old) => ({ ...old, [move.id]: true }));
    const clientRef = newReference('v2-done');
    try {
      const response = await api.post('/api/noke/restock-v2/done', {
        client_ref: clientRef, kind: TODAY_KIND[move.group], variant_id: line.variant_id, qty: line.qty,
      });
      // Keep the ref and the window: that pair is the whole of what 復原 needs.
      const until = (response?.data as { undoable_until?: string | null } | undefined)?.undoable_until ?? null;
      setDoneRefs((old) => ({ ...old, [move.id]: { client_ref: clientRef, undoable_until: until } }));
      const siblings = movesOfFixture(moves, move.fixtureId, true);
      if (siblings.length && siblings.every((row) => row.id === move.id || doneRef.current[row.id])) {
        enqueueSnackbar(`${move.fixtureLabel} 做完了 → 走下一個架子`, { variant: 'success' });
      }
    } catch (err) {
      setDoneRows((old) => { const next = { ...old }; delete next[move.id]; return next; });
      enqueueSnackbar(readNokeError(err, '登記失敗，請重試。'), { variant: 'error' });
    } finally {
      setPendingRows((old) => { const next = { ...old }; delete next[move.id]; return next; });
    }
  }, [enqueueSnackbar, moves]);

  // 復原 is a REAL undo now (POST /restock-v2/undo → the compensating transfer + the previous
  // on_display). The card unticks first; if the server refuses (already undone, past 12 hours, stock
  // moved on) it goes straight back to 已完成, because the stock write is still standing.
  const undoDone = useCallback(async (move: RestockV2Move) => {
    setPendingRows((old) => ({ ...old, [move.id]: true }));
    setDoneRows((old) => { const next = { ...old }; delete next[move.id]; return next; });
    const ok = await veto.undo(move, doneRefs[move.id]);
    if (ok) setDoneRefs((old) => { const next = { ...old }; delete next[move.id]; return next; });
    else setDoneRows((old) => ({ ...old, [move.id]: true }));
    setPendingRows((old) => { const next = { ...old }; delete next[move.id]; return next; });
  }, [veto, doneRefs]);

  const toggleWhy = useCallback((id: string) => {
    setWhyRows((old) => ({ ...old, [id]: !old[id] }));
  }, []);

  const toggleZone = useCallback((key: string) => {
    setOpenZones((old) => ({ ...old, [key]: !(old[key] === undefined ? key === firstZone : old[key]) }));
  }, [firstZone]);

  // 這次不訂 / 這次要訂. Local, instant, reversible, and it writes NOTHING — the reason Magnus could
  // not tick a line back was that this control used to call the permanent veto, after which the
  // recompute dropped the style out of order.groups and the checkbox itself unmounted.
  // 全部取消／全部恢復 over the rows on screen. Local to this screen like every other order edit,
  // so a mis-tap costs one tap back and never touches the shared 不要這款 veto.
  const omitAllOrderLines = useCallback((skus: string[], omit: boolean) => {
    setOrderEdit((old) => (omit ? omitAllLines(old, skus) : restoreAllLines(old, skus)));
  }, []);

  const toggleOrderLine = useCallback((sku: string) => {
    setOrderEdit((old) => toggleOrderLineEdit(old, sku));
  }, []);

  // 不要這款 — the standing decision, unchanged: the shared veto Adam sees too. It stays on the row
  // as its own, heavier control, and 加回 is how it comes back while it is still in flight.
  const vetoStyle = useCallback((sku: string) => {
    if (veto.state.plan[sku]) veto.addBackStyle(sku); else veto.excludeStyle(sku);
  }, [veto]);

  const editVariantQty = useCallback((sku: string, variant: RestockV2OrderVariant, next: number) => {
    setOrderEdit((old) => setVariantQty(old, sku, variant, next));
  }, []);

  const resetLine = useCallback((line: RestockV2OrderLine) => {
    setOrderEdit((old) => resetLineQty(old, line));
  }, []);

  // 加顏色／尺寸 (Magnus 2026-09-18: 「there is still no way to edit to a specific size and 尺寸 that
  // wasn't shown」). Same state, same single edit path as a stepper — the row joins `line.variants`
  // inside `applyOrderEdit`, so nothing downstream knows or needs to know it was hand-added.
  const addOrderVariant = useCallback((sku: string, row: RestockV2Addable) => {
    setOrderEdit((old) => addVariant(old, sku, addableOrderVariant(row)));
  }, []);

  const removeOrderVariant = useCallback((sku: string, variantId: number) => {
    setOrderEdit((old) => removeAddedVariant(old, sku, variantId));
  }, []);

  // An uncertain response must retry the exact original payload, including quantities.
  const createOrder = useCallback(async () => {
    if (submittingOrder.current) return;
    if (!pendingOrder.current) {
      const lines = koreaOrderedLines(plan, excluded);
      if (!lines.length) return;
      pendingOrder.current = { client_ref: newReference('v2-counter-order'),
        lines: lines.map((line) => ({ ...line })), text: orderCopyText(plan, excluded) };
    }
    const request = pendingOrder.current;
    try { sessionStorage.setItem(ORDER_STORAGE, JSON.stringify(request)); }
    catch {
      enqueueSnackbar('無法保存訂單重試資料，請確認瀏覽器儲存空間後重試。', { variant: 'error' });
      return;
    }
    const pieces = request.lines.reduce((sum, row) => sum + row.qty, 0);
    submittingOrder.current = true;
    setBusy(true);
    try {
      const response = await api.post<{ order?: RestockV2CounterOrder }>(
        '/api/noke/restock-v2/counter-order', { client_ref: request.client_ref, lines: request.lines },
      );
      const order = response?.data?.order;
      setCounterOrder(order
        ? { ...order, client_ref: order.client_ref || request.client_ref }
        : { id: null, client_ref: request.client_ref, line_count: request.lines.length, piece_count: pieces });
      pendingOrder.current = null;
      try { sessionStorage.removeItem(ORDER_STORAGE); } catch { /* Server success remains authoritative. */ }
      setOrderUncertain(false);
      setCreatedOrderText(request.text);
      setOrderEdit(EMPTY_ORDER_EDIT);
      enqueueSnackbar(`已建立門市訂單（${request.lines.length} 筆 / ${pieces} 件），出貨人員可在收貨頁查看。`, { variant: 'success' });
      notifyOrderChanged();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const refused = status != null && status >= 400 && status < 500 && ![408, 409, 429].includes(status);
      if (refused) {
        pendingOrder.current = null;
        try { sessionStorage.removeItem(ORDER_STORAGE); } catch { /* No order was created. */ }
      }
      setOrderUncertain(!refused);
      enqueueSnackbar(refused ? readNokeError(err, '訂單未建立，請修正後重試。')
        : `${readNokeError(err, '尚未確認建立結果。')}請重試確認原訂單；會使用原本的色碼與數量。`, { variant: 'error' });
    } finally { submittingOrder.current = false; setBusy(false); }
  }, [plan, excluded, enqueueSnackbar]);

  const copyOrder = useCallback((text: string) => {
    void copy(pendingOrder.current?.text || createdOrderText || text);
  }, [copy, createdOrderText]);

  /** Cancel an explicitly created order and its outstanding lines. */
  const undoCounterOrder = useCallback(async () => {
    const order = counterOrder;
    if (!order) return;
    setBusy(true);
    try {
      await api.post('/api/noke/restock-v2/counter-order/cancel', {
        client_ref: order.client_ref, id: order.id ?? undefined,
      });
      setCounterOrder(null);
      setCreatedOrderText(null);
      pendingOrder.current = null;
      setOrderUncertain(false);
      enqueueSnackbar('已復原，這張門市訂單取消了，這些款會回到下次建議。', { variant: 'success' });
      // The cancel stamped cancelled_at on the LINES too, so the planner's in-transit filter drops
      // them and they come back into the order. Proving that takes a recompute.
      notifyOrderChanged();
    } catch (err) {
      enqueueSnackbar(readNokeError(err, '復原失敗，請重試。'), { variant: 'error' });
    } finally { setBusy(false); }
  }, [counterOrder, enqueueSnackbar]);

  const adopt = useCallback(async () => {
    setBusy(true);
    try {
      await api.post('/api/noke/restock-v2/adopt', { client_ref: newReference('v2-adopt') });
      setRackId(null);
      enqueueSnackbar('已採用這版，換下清單已進入「今天」。', { variant: 'success' });
      await fetchPlan();
    } catch (err) {
      enqueueSnackbar(readNokeError(err, '採用失敗，請重試。'), { variant: 'error' });
    } finally { setBusy(false); }
  }, [enqueueSnackbar, fetchPlan]);

  const lookupWhy = useCallback(async (sku: string) => {
    setWhyLoading(true);
    setWhyError('');
    setWhy(null);
    try {
      const response = await api.get<RestockV2Why>(`/api/noke/restock-v2/why/${encodeURIComponent(sku)}`);
      setWhy(response.data);
    } catch (err) {
      setWhyError(readNokeError(err, '查不到這個 SKU，請確認後重試。'));
    } finally { setWhyLoading(false); }
  }, []);

  const startWalk = useCallback(() => {
    if (firstZone) setOpenZones((old) => ({ ...old, [firstZone]: true }));
    setScreen('today');
  }, [firstZone]);

  const openRack = useCallback((fixtureId: string) => {
    if (desktop) setSelectedZone(fixtureId); else setRackId(fixtureId);
  }, [desktop]);

  // Desktop lands on the rack with the most to do, so the right column is never empty.
  const busiestZone = useMemo(() => {
    const ranked = walk
      .map((id) => ({ id, count: movesOfFixture(moves, id, true).length }))
      .sort((a, b) => b.count - a.count);
    return ranked.length ? ranked[0].id : null;
  }, [walk, moves]);
  const shownZone = selectedZone && walk.indexOf(selectedZone) >= 0 ? selectedZone : busiestZone;

  const leftToDo = todo.filter((move) => !doneRows[move.id]).length;
  const badges: Record<ScreenKey, number> = {
    map: walk.filter((id) => movesOfFixture(moves, id, true).length > 0).length,
    today: leftToDo,
    order: koreaTotalKept(plan?.order, excluded),
    // How many yes/no answers are waiting for him. 0 on a backend without noke_counter_list.
    list: pendingDecisions(plan),
  };
  // Supplier links are Magnus's and Adam's only; the hook itself makes no request for anyone else.
  //
  // ONE hook for both screens that show links (Magnus 2026-09-17: 「also should have sinsang link at
  // the list」). The SKUs differ — 訂貨 asks about what it is about to order, 選品 about what he has
  // approved — so the screen picks the line set, and the capability check, the endpoint, the
  // 門市人員 gate and the focus re-check are all the single existing path. Enabled only on the two
  // screens that render links, so 衣架 and 今天 still make no request at all.
  const sourcingLines = useMemo(
    () => (screen === 'list' ? approvedRows(counterList(plan)) : orderableLines(plan)),
    [screen, plan],
  );
  const sourcing = useSourcing(sourcingLines, screen === 'order' || screen === 'list');

  return (
    <Box sx={{ bgcolor: C.paper, color: C.ink, mx: { xs: -2, sm: -3 }, mt: -2, minHeight: '70vh' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 40, bgcolor: C.ink, borderBottom: '2px solid', borderColor: C.back }}>
        <Box
          sx={{
            maxWidth: desktop ? 1700 : 720, mx: 'auto', display: 'flex', alignItems: 'baseline', gap: 1,
            px: desktop ? 3 : 1.75, pt: 1.25, pb: 1,
          }}
        >
          <Typography component="h1" sx={{ fontWeight: 900, letterSpacing: '.06em', fontSize: 15, color: C.onInk }}>
            NOKE<Box component="span" sx={{ color: C.back }}>／</Box>補貨
          </Typography>
          <Typography sx={{ ml: 'auto', fontSize: 11, color: C.onInk2, fontFamily: MONO, letterSpacing: '.02em' }}>
            {plan
              ? `${monthDay(plan.generated_at)} · 下次下單 ${weekdayLabel(plan.meta?.next_order_night)} ${monthDay(plan.meta?.next_order_night)} 晚`
              : '正在計算'}
          </Typography>
          <Box
            component="button" type="button" aria-label="調整計算設定" disabled={!plan}
            onClick={() => setSettingsOpen(true)}
            sx={{
              display: 'inline-flex', alignItems: 'center', bgcolor: 'transparent', border: 0,
              color: C.onInk2, cursor: 'pointer', p: 0, ml: 0.5,
            }}
          >
            <SettingsIcon fontSize="small" />
          </Box>
        </Box>
        <Box
          role="tablist" aria-label="補貨畫面"
          sx={{ maxWidth: desktop ? 1700 : 720, mx: 'auto', display: 'flex', px: desktop ? 3 : 1.25, gap: desktop ? 1 : 0 }}
        >
          {SCREEN_KEYS.map((key) => {
            const active = screen === key;
            return (
              <Box
                key={key} component="button" type="button" role="tab" aria-selected={active}
                onClick={() => setScreen(key)}
                sx={{
                  flex: desktop ? '0 0 auto' : 1, minWidth: desktop ? 170 : 0,
                  appearance: 'none', background: 'none', border: 0, cursor: 'pointer',
                  pt: 1.1, pb: 1.4, px: desktop ? 0.5 : 0.25,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: desktop ? 0.75 : 0.4, fontWeight: 700, fontSize: desktop ? 13.5 : 12,
                  // 衣架地圖 wrapped to two lines at 400px and made its tab taller than the others.
                  whiteSpace: 'nowrap', overflowWrap: 'normal', wordBreak: 'keep-all',
                  color: active ? '#FFF8EA' : '#A99E8C',
                  borderBottom: '3px solid', borderColor: active ? C.back : 'transparent',
                }}
              >
                {SCREEN_LABEL[key]}
                <Box
                  component="span"
                  sx={{
                    fontFamily: MONO, fontSize: 11, fontWeight: 600, borderRadius: '99px', px: '5px',
                    minWidth: 22, bgcolor: active ? C.back : '#332C22', color: active ? '#fff' : '#EADFCB',
                  }}
                >
                  {badges[key]}
                </Box>
              </Box>
            );
          })}
        </Box>
        {/* 數字待重算 — the honest half of not refreshing on every tap. The rows he acted on are
            already right; the engine-derived numbers are not, and they say so instead of pretending.
            It lives INSIDE the sticky header: as its own sticky sibling at the same top it slid
            underneath and rendered as a two-pixel sliver. */}
        {plan && !loading && (numbersPending || refreshing) && (
          <Box
            role="note" aria-label="數字待重算"
            sx={{ bgcolor: C.waitWash, borderTop: '1px solid', borderColor: '#E5D3A8' }}
          >
            <Box
              sx={{
                maxWidth: desktop ? 1700 : 720, mx: 'auto', px: desktop ? 3 : 1.5, py: 0.6,
                display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: C.wait, whiteSpace: 'nowrap' }}>
                {refreshing ? '重新計算中…' : '數字待重算'}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: C.ink2, minWidth: 0 }}>
                你的決定已經存好了。件數、秋冬比例和訂貨數量要等引擎重算。
              </Typography>
              {!refreshing && (
                <Box
                  component="button" type="button" onClick={refreshNow} aria-label="重新計算"
                  sx={{
                    ml: 'auto', flex: 'none', appearance: 'none', cursor: 'pointer', borderRadius: '7px',
                    px: 1.2, py: '4px', fontSize: 12, fontWeight: 800, border: '1px solid',
                    borderColor: C.wait, bgcolor: C.card, color: C.wait,
                  }}
                >
                  重新計算
                </Box>
              )}
              {refreshing && <CircularProgress size={13} sx={{ ml: 'auto', flex: 'none', color: C.wait }} />}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ maxWidth: desktop ? 1700 : 720, mx: 'auto', px: desktop ? 3 : 1.5, pt: 1.5, pb: 4 }}>
        {loading && (
          <Box role="status" sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={28} /><Typography sx={{ mt: 1 }}>正在計算今天要動什麼…</Typography>
          </Box>
        )}
        {error && !loading && (
          <Alert severity="error" sx={{ my: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchPlan()}>重試</Button>}>{error}</Alert>
        )}

        {plan && !loading && screen === 'map' && (
          <FloorMap
            plan={plan} moves={moves} doneRows={doneRows} pendingRows={pendingRows} whyRows={whyRows}
            thumbIndex={thumbIndex} desktop={desktop} busy={busy} selectedId={desktop ? shownZone : null}
            onZone={openRack} onWalk={startWalk} onAdopt={adopt} onDone={markDone}
            onUndo={undoDone} onSkip={veto.skipToday} skipBusy={skipBusy} veto={vetoControls}
            excludedStyles={excludedByFixture[shownZone || ''] || []} onAddBack={veto.addBackStyle}
            keptOffRail={keptByFixture[shownZone || ''] || []} onUnkeepRow={unkeepRow}
            onWhy={toggleWhy} onImage={setGalleryTarget}
          />
        )}
        {plan && !loading && screen === 'today' && (
          <TodayScreen
            plan={plan} moves={moves} doneRows={doneRows} pendingRows={pendingRows} whyRows={whyRows}
            openZones={openZones} defaultOpenId={firstZone} desktop={desktop} wide={wide}
            onToggleZone={toggleZone} onDone={markDone}
            onUndo={undoDone} onSkip={veto.skipToday} skipBusy={skipBusy} skipPending={veto.pending}
            onAddBackMove={veto.addBackMove} onWhy={toggleWhy} onCopy={copy} onImage={setGalleryTarget}
          />
        )}
        {plan && !loading && screen === 'order' && (
          <OrderScreen
            plan={plan} busy={busy} orderPending={refreshing || numbersPending} why={why} whyLoading={whyLoading} whyError={whyError}
            counterOrder={counterOrder} orderUncertain={orderUncertain}
            savedOrderText={pendingOrder.current?.text || createdOrderText}
            excluded={excluded} excludePending={veto.pending} sourcing={sourcing}
            edit={orderEdit} desktop={desktop} wide={wide}
            onToggleOrder={toggleOrderLine} onOmitAll={omitAllOrderLines}
            onVeto={vetoStyle} onVariantQty={editVariantQty}
            onResetLine={resetLine} onAddVariant={addOrderVariant} onRemoveVariant={removeOrderVariant}
            onAddBack={veto.addBackStyle}
            onCopy={copyOrder} onCreateOrder={createOrder} onNextOrder={() => { setCounterOrder(null); setCreatedOrderText(null); }} onUndoOrder={undoCounterOrder} onWhy={lookupWhy}
            onImage={setGalleryTarget} onOutletPulled={refreshNow}
          />
        )}
        {plan && !loading && screen === 'list' && (
          <ListScreen
            plan={plan} list={counterList(plan)} writes={listWrites} pool={pool}
            desktop={desktop} wide={wide}
            veto={{ ...vetoControls, onReadd: veto.addBackVariant }} sourcing={sourcing}
            onImage={setGalleryTarget}
          />
        )}

        {plan && !loading && (
          <Box sx={{ mt: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" variant="outlined" disabled={loading || busy} onClick={refreshNow}>更新</Button>
            <Typography sx={{ fontSize: 11, color: C.ink3 }}>
              資料更新：{new Date(plan.generated_at).toLocaleString('zh-TW')}
            </Typography>
          </Box>
        )}
      </Box>

      {plan && !desktop && (
        <RackSheet
          fixtureId={rackId} plan={plan} moves={moves} walk={walk} doneRows={doneRows}
          pendingRows={pendingRows} whyRows={whyRows} thumbIndex={thumbIndex} busy={busy}
          onClose={() => setRackId(null)} onAdopt={adopt} onDone={markDone} onUndo={undoDone}
          onSkip={veto.skipToday} skipBusy={skipBusy} veto={vetoControls}
          excludedStyles={excludedByFixture[rackId || ''] || []}
          keptOffRail={keptByFixture[rackId || ''] || []} onUnkeepRow={unkeepRow}
          onAddBack={veto.addBackStyle} onWhy={toggleWhy} onImage={setGalleryTarget}
        />
      )}
      {plan && (
        <SettingsDialog
          open={settingsOpen} plan={plan} params={params} serverError={paramError}
          onClose={() => { setSettingsOpen(false); setParamError(''); }}
          onApply={(next) => { setParamError(''); setParams(next); }}
        />
      )}

      <GalleryDialog target={galleryTarget} onClose={() => setGalleryTarget(null)} />

      <Dialog open={copyText !== null} onClose={() => setCopyText(null)} maxWidth="sm" fullWidth>
        <DialogTitle>請手動複製清單</DialogTitle>
        <DialogContent>
          <TextField multiline fullWidth minRows={10} value={copyText || ''} inputProps={{ 'aria-label': '可複製的補貨清單', readOnly: true }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setCopyText(null)}>關閉</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestockV2Tab;
